import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  useContext
} from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { matchPath } from 'react-router'
import { Route } from 'react-router-dom'
import FocusTrap from 'focus-trap-react'
import { noop } from 'lodash'
import { scrollToElement } from 'src/helpers/browser'
import { useUniqueId } from 'src/hooks'
import BodyScrollLock from 'src/Components/shared/BodyScrollLock'
import Panel from 'src/Components/shared/Panel'
import OverlayDefaultHeader from './Headers/OverlayDefaultHeader'
import { ActiveOverlayContext } from './OverlayContext'

import styles from './OverlayPanel.module.scss'

export const OverlayRouter = React.forwardRef(
  ({ title, renderTitle, path, onOpen, onClose, ...restProps }, ref) => {
    const id = useUniqueId(`Overlay_${!!title && title.replace(/\s/g, '_')}_`)
    const [direct, setDirect] = useState(
      !!matchPath(window.location.pathname, { path })
    )
    const [, setOpen] = useState(false)
    const { updateOverlayState } = useContext(ActiveOverlayContext) || {
      updateOverlayState: noop
    }

    const handleOpen = useCallback(() => {
      setOpen(true)
      updateOverlayState({ id, data: { title } })
      typeof onOpen === 'function' && onOpen()
    }, [onOpen, updateOverlayState, id, title])

    const handleClose = useCallback(() => {
      setOpen(false)
      setDirect(false)
      updateOverlayState({ id })
      typeof onClose === 'function' && onClose()
    }, [onClose, updateOverlayState, id])

    return (
      <Route
        path={path}
        render={args => (
          <OverlayInner
            id={id}
            forwardedRef={ref}
            handleOpen={handleOpen}
            handleClose={handleClose}
            args={args}
            direct={direct}
            title={title}
            renderTitle={renderTitle}
            {...restProps}
          />
        )}
      />
    )
  }
)

export const OverlayInner = ({
  component: Component,
  render,
  renderHeader,
  renderTitle,
  id,
  args,
  title,
  back,
  direct,
  anchorOffset,
  className,
  handleOpen,
  handleClose,
  forwardedRef
}) => {
  const fallbackRef = useRef(null)
  const { overlays } = useContext(ActiveOverlayContext) || { overlays: {} }

  const doNavBack = useCallback(
    history => {
      if (direct && back) {
        history.push(back)
      } else {
        history.goBack()
      }
    },
    [back, direct]
  )

  if (!overlays[id]) {
    handleOpen()
  }

  useImperativeHandle(
    forwardedRef,
    () => ({
      back: () => doNavBack(args.history)
    }),
    [args, doNavBack]
  )

  useEffect(() => {
    return () => handleClose()
  }, [handleClose])

  useEffect(() => {
    if (direct) {
      const element = forwardedRef ? forwardedRef.current : fallbackRef.current
      scrollToElement(element)
      return () => scrollToElement(element, { padding: anchorOffset || 0 })
    }
  }, [forwardedRef, direct, anchorOffset])

  document.documentElement.style.width = '100vw'
  document.documentElement.style.height = '100vh'
  document.documentElement.style.overflow = 'hidden'
  document.documentElement.style.position = 'fixed'
  document.documentElement.style.top = '0'
  useEffect(() => {
    return () => {
      document.documentElement.style.removeProperty('width')
      document.documentElement.style.removeProperty('height')
      document.documentElement.style.removeProperty('overflow')
      document.documentElement.style.removeProperty('position')
      document.documentElement.style.removeProperty('top')
    }
  }, [])

  const renderHeaderComponent = () => {
    const props = {
      goBack: () => doNavBack(args.history),
      renderTitle,
      title,
      ...args
    }

    if (renderHeader) {
      return renderHeader(props)
    } else {
      return <OverlayDefaultHeader {...props} />
    }
  }

  return (
    <div
      id={id}
      ref={forwardedRef || fallbackRef}
      className={styles.overlayAnchor}
    >
      <FocusTrap
        focusTrapOptions={{
          allowOutsideClick: () => true,
          initialFocus: 'div'
        }}
      >
        <BodyScrollLock allowTouchMove>
          {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
          <div tabIndex='0' />
          {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
          <div
            className={classnames(
              styles.overlayCurtain,
              overlays[id] && overlays.activeId !== id && styles.hidden
            )}
          >
            {renderHeaderComponent()}
            <Panel className={classnames(styles.overlayPanel, className)}>
              {Component && <Component {...args} />}
              {!Component && render && render(args)}
            </Panel>
          </div>
        </BodyScrollLock>
      </FocusTrap>
    </div>
  )
}

OverlayRouter.propTypes = {
  title: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  renderTitle: PropTypes.func, // Overwrites how title is rendered
  onOpen: PropTypes.func,
  onClose: PropTypes.func
}

OverlayInner.propTypes = {
  component: PropTypes.oneOfType([PropTypes.func, PropTypes.element]),
  render: PropTypes.func,
  renderHeader: PropTypes.func,
  renderTitle: PropTypes.func,
  id: PropTypes.string,
  args: PropTypes.shape({
    history: PropTypes.array
  }),
  title: PropTypes.func,
  back: PropTypes.string, // default route to back when no history provided
  direct: PropTypes.bool, // loaded overlay's path directly (not navigated to via history)
  anchorOffset: PropTypes.number, // vertical offset of anchor for position in digest
  className: PropTypes.string,
  handleOpen: PropTypes.func,
  handleClose: PropTypes.func,
  forwardedRef: PropTypes.shape({
    current: PropTypes.object
  })
}

export default OverlayRouter
